import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import loadingSvg from "../Assets/images/tube-spinner.svg";
import { useTranslation } from "react-i18next";
import emissionViewStore from "../Store/marco/emissionViewStore";
import eFManagementStore from "../Store/marco/emissionFactorManagementStore";
import commonStore from "../Store/marco/commonStore";
import guidanceForEmissionActivityStore from "../Store/marco/guidanceForEmissionActivityStore";
import { useJobPolling } from "./hooks/useJobPolling";
import handleAPIError, {
  MESSAGE_CODES,
  showSuccessToast,
} from "../services/toast";
//icon
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import { importJobAPI } from "./API/importJobAPI";
import LoadingWithLabel from "./components/LoadingWithLabel";

//main
export const ExcelImportExportModal = observer(() => {
  const store = emissionViewStore;
  const cmStore = commonStore;
  const { t } = useTranslation("common");

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(t("Choose a file (.xlsx)"));
  const [downloadProgress, setDownloadProgress] = useState(0); //模擬下載進度
  const intervalRef = useRef(null); // 用來存放計時器

  const { jobInfo, isPolling, startPolling, stopPolling } = useJobPolling(
    (jobId) => importJobAPI.checkJobStatus(jobId),
    2000,
    (data, apiData) => {
      // 任務完成時執行
      const messageCode = apiData?.messageCode ?? apiData?.data?.messageCode;
      if (
        messageCode ===
        MESSAGE_CODES.EMISSION_INVENTORY_IMPORT_COMPLETE_WITH_ERRORS
      ) {
        handleAPIError(null, { apiData, throwError: false });
      } else {
        showSuccessToast({
          apiData,
          scenarioCode: MESSAGE_CODES.EMISSION_INVENTORY_IMPORT_COMPLETE,
        });
      }
      //重新載入資料
      if (store.selectedView === "Emission Activity") {
        store.getEAListData();
      } else if (cmStore.excelImportType === "dataEntry") {
        store.getDEListData();
      } else if (cmStore.excelImportType === "ef") {
        store.getEFListData();
      } else if (cmStore.excelImportType === "custom") {
        eFManagementStore.getCustomEFData();
      } else if (cmStore.excelImportType === "school_boundary_site") {
        guidanceForEmissionActivityStore.getBoundarySiteData();
      } else if (
        cmStore.excelImportType === "school_activity_source" ||
        cmStore.excelImportType === "school_activity_list"
      ) {
        guidanceForEmissionActivityStore.getEmissionActivityList(
          cmStore.rootId,
          guidanceForEmissionActivityStore.selectedCom,
          guidanceForEmissionActivityStore.selectedYear,
        );
      }
    },
    (error, apiData) => {
      handleAPIError(error, { apiData, throwError: false });
    },
  );

  ////////選擇全部或是某個cat
  const [isAll, setIsAll] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const handleAction = async (actionType) => {
    // 1. 統一取得目前的 type
    const type =
      store.selectedView === "Emission Activity"
        ? "EmissionActivity"
        : cmStore.excelImportType;

    // const currentCat = store.selectedTab ? store.selectedTab.split(" ")[0] : "";

    //大專院校共用的配置
    const createSchoolAction = () => ({
      download: () =>
        guidanceForEmissionActivityStore.downloadGFEAExcel(cmStore.rootId),
      upload: (file) =>
        guidanceForEmissionActivityStore.bulkUploadGFEAExcel(
          cmStore.excelImportType,
          file,
        ),
    });

    // //  Data Entry 特定分類的擴充對應表
    // const dataEntrySpecialHandlers = {
    //   3.3: {
    //     // 請將此處替換為你 Store 中實際針對 3.3 寫的 API Function
    //     download: () => store.downloadDEExcel_3_3(cmStore.lang, cmStore.rootId),
    //     upload: (file) =>
    //       store.bulkUploadCat33Excel(
    //         cmStore.excelImportType,
    //         file,
    //         cmStore.lang,
    //       ),
    //   },
    //   // "3.4": {
    //   //   download: () => store.downloadCat34Excel(cmStore.lang, cmStore.rootId),
    //   //   upload: (file) => store.bulkUploadCat34Excel(cmStore.excelImportType, file, cmStore.lang),
    //   // },
    // };

    // 2. 定義行為矩陣
    const actions = {
      EmissionActivity: {
        download: () => store.downloadEAExcel(cmStore.lang),
        upload: (file) =>
          store.bulkUploadEAExcel(cmStore.excelImportType, cmStore.lang, file),
      },
      dataEntry: {
        download: () =>
          store.downloadDEExcel(cmStore.lang, cmStore.rootId, isAll),
        upload: (file) =>
          store.bulkUploadDEExcel(cmStore.excelImportType, file, cmStore.lang),
      },
      ef: {
        download: () => store.downloadEFExcel(cmStore.lang, isAll),
        upload: (file) =>
          store.bulkUploadEFExcel(cmStore.excelImportType, file),
      },
      custom: {
        download: () => eFManagementStore.downloadCustomEFExcel(),
        upload: (file) =>
          eFManagementStore.bulkUploadCustomEFExcel(
            cmStore.excelImportType,
            file,
          ),
        // 之後要改成跳出警告視窗的
        // eFManagementStore._handleSetIsShowCustomEFExcelModal(true);
      },
      ////大專院校
      school_boundary_site: createSchoolAction(),
      school_activity_source: createSchoolAction(),
      school_activity_list: createSchoolAction(),
    };

    // 3. 根據傳入的 'download' 或 'upload' 執行對應函式
    const target = actions[type];
    if (target && target[actionType]) {
      return await target[actionType](file); // file 僅在上傳時會用到
    }
    return null; // 如果找不到對應的 action
  };

  const handleDownload = async () => {
    // 1. 歸零並啟動計時器 (Loading 狀態由 Store 的函數內部自己去觸發，這裡不管)
    setDownloadProgress(0);
    let currentProgress = 0;

    intervalRef.current = setInterval(() => {
      if (currentProgress < 95) {
        currentProgress += Math.random() * 5 + 1;
        if (currentProgress > 95) currentProgress = 95;
        setDownloadProgress(Math.floor(currentProgress));
      }
    }, 800);

    // 2. 執行下載
    const result = await handleAction("download");
    clearInterval(intervalRef.current);
    intervalRef.current = null;

    // 判斷成功或失敗
    if (result !== null) {
      // 成功
      setDownloadProgress(100);
      await new Promise((resolve) => setTimeout(resolve, 500));
    } else {
      // 失敗：Store 回傳了 null
      console.warn("下載失敗，進度歸零");
      setDownloadProgress(0);
    }

    // 稍微延遲一下再把數字歸零
    setTimeout(() => setDownloadProgress(0), 500);
  };

  const handleUpload = async (file) => {
    const result = await handleAction("upload", file);
    console.log("Upload result:", result); // 檢查回傳值
    if (result) {
      startPolling(result);
    } else {
      console.error("No jobId found in response");
    }
  };

  // 檢查是否有現有的上傳任務
  const checkExistingJob = async () => {
    try {
      const data = await importJobAPI.checkImportJob(cmStore.excelImportType);
      if (data !== null && data.jobId) {
        startPolling(data.jobId);
      }
    } catch (error) {
      console.log("Check existing job error:", error);
    }
  };

  // 一開啟modal就去判斷目前是否有上傳中的檔案
  useEffect(() => {
    checkExistingJob();
    return () => {
      stopPolling(); // 關閉 modal 時停止輪詢
    };
  }, []);

  // 清理計時器的副作用:防止如果 Modal 被強制關閉，確保計時器停止
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw", // 讓背景覆蓋整個視窗
        height: "100vh",
        background: "rgba(0, 0, 0, 0.5)", // 半透明背景
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
          width: "800px",
          height: isPolling ? "350px" : "300px",
          position: "relative",
        }}
      >
        {(emissionViewStore.emissionActivityModalLoading ||
          eFManagementStore.isExcelModalLoading ||
          guidanceForEmissionActivityStore.isExcelModalLoading ||
          downloadProgress > 0) && (
          <LoadingWithLabel progress={downloadProgress} />
        )}

        <h3
          style={{
            textAlign: "left",
            color: "black",
            paddingLeft: "15px",
            paddingTop: "20px",
            height: "80px",
            margin: "0",
            width: "100%",
            borderBottom: "1px solid #ccc",
          }}
        >
          {t("Excel Import and Export")}

          {/* Radio buttons */}
          {cmStore.excelImportType === "ef" ? (
            <div
              style={{
                display: "flex",
                right: "230px",
                top: "0",
                width: "180px",
                alignItems: "center",
                height: "80px",
                position: "absolute",
              }}
            >
              <label
                style={{
                  fontSize: "16px",
                  width: "60px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  value="all"
                  checked={isAll}
                  onChange={() => setIsAll(true)}
                  style={{ marginRight: "10px" }}
                />
                {t("All")}
              </label>
              <label
                title={
                  emissionViewStore.dataEntryListData?.fullList.length > 0 ||
                  emissionViewStore.emissionFactorListData?.fullList.length > 0
                    ? ""
                    : t("No data in the selected category.")
                }
                style={{
                  fontSize: "16px",
                  width: "120px",
                  textAlign: "right",
                  cursor:
                    emissionViewStore.dataEntryListData?.fullList.length > 0
                      ? "pointer"
                      : "not-allowed",
                }}
              >
                <input
                  type="radio"
                  value="category"
                  checked={!isAll}
                  onChange={() => setIsAll(false)}
                  style={{ marginRight: "10px" }}
                  disabled={
                    emissionViewStore.dataEntryListData?.fullList.length > 0 ||
                    emissionViewStore.emissionFactorListData?.fullList.length >
                      0
                      ? false
                      : true
                  }
                />
                {`${t("Category")} ${
                  emissionViewStore.selectedTab.split(" ")[0]
                }`}
              </label>
            </div>
          ) : null}

          <div
            style={{
              float: "right",
              width: "185px",
              height: "43px",
              fontSize: "16px",
              backgroundColor: "rgb(40,60,130)",
              color: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontWeight: "400",
              marginRight: "20px",
              borderRadius: "5px",
              cursor: "pointer",
            }}
            onClick={handleDownload}
          >
            {t("Export")}
          </div>
        </h3>
        <main
          style={{
            textAlign: "left",
            color: "black",
            paddingLeft: "15px",
            paddingRight: "15px",
            width: "100%",
            height: isPolling ? "200px" : "150px",
            borderBottom: "1px solid #ccc",
          }}
        >
          {/* title */}
          <div
            style={{
              paddingTop: "15px",
              marginBottom: "10px",
            }}
          >
            {t("New File Upload")}
          </div>
          {/* input area */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontFamily: "Arial, sans-serif",
              marginBottom: "16px",
            }}
          >
            <div style={{ display: "block", fontFamily: "Arial, sans-serif" }}>
              <input
                type="file"
                id="fileInput"
                accept=".xlsx"
                style={{ display: "none" }}
                onChange={(e) => {
                  handleFileChange(e);
                }}
              />
              <label
                htmlFor="fileInput"
                style={{
                  display: "inline-block",
                  padding: "10px 15px",
                  border: "1px solid #d1d3e2",
                  borderRadius: "5px",
                  backgroundColor: "#ffffff",
                  color: "#6c757d",
                  fontSize: "16px",
                  cursor: "pointer",
                  width: "500px",
                  height: "45px",
                  textAlign: "left",
                }}
              >
                {fileName}
              </label>
            </div>
            <button
              style={{
                width: "185px",
                height: "43px",
                fontSize: "16px",
                backgroundColor:
                  file && !isPolling ? "rgb(40,60,130)" : "rgb(118,128,176)",
                color: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "400",
                // marginLeft: "60px",
                borderRadius: "5px",
                cursor: file && !isPolling ? "pointer" : "not-allowed",
              }}
              disabled={!file || isPolling}
              onClick={handleUpload}
            >
              {t("Import")}
            </button>
          </div>
          {/* upload progress area */}
          {isPolling && jobInfo && (
            <>
              <div
                style={{
                  // paddingTop: "15px",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  // marginBottom: "10px",
                  backgroundColor: "#f0f0f0",
                  height: "60px",
                  // width: "745px",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    gap: "8px",
                  }}
                >
                  <InsertDriveFileIcon sx={{ color: "#00B050" }} />{" "}
                  {t("Uploading...") + " " + jobInfo.progress + "%"}
                </div>
                <div
                  style={{
                    paddingRight: "8px",
                  }}
                >
                  <CircularProgress
                    // enableTrackSlot
                    size="30px"
                    variant="determinate"
                    value={jobInfo.progress}
                  />
                  {/* {t("Uploading...")} */}
                </div>
              </div>
              <LinearProgress
                variant="determinate"
                value={jobInfo.progress}
                color="success"
                sx={{ height: "10px" }}
              />
            </>
          )}
        </main>
        <div
          style={{
            display: "flex",
            justifyContent: "right",
            alignItems: "center",
            height: "70px",
          }}
        >
          <button
            style={{
              width: "70px",
              height: "40px",
              backgroundColor: "rgb(136,136,136)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginRight: "30px",
            }}
            onClick={() => {
              cmStore._handleSetIsShowExcelImportExportModal(false);
            }}
          >
            {t("Close")}
          </button>
        </div>
      </div>
    </div>
  );
});
